import type { FC } from 'react';

import type { FilesHttpGetStoragesQueryPatched } from '@iconik-io/iconik-api/src/schema_patched/files_storages';
import type {
  MultiSelectFilterModifier,
  MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import type { FormFieldEvent } from '../../types';
import { MultiSelectFilterFieldWrapper } from '../multi_select_filter_field/multi_select_filter_field_wrapper';
import { useStorageFilterOptions } from '../multi_select_filter_field/option_hooks';

type Props = {
  name: string;
  value?: MultiSelectFilterValue;
  onChange?: (
    value: MultiSelectFilterValue | undefined,
    event?: FormFieldEvent,
  ) => void;
  params?: FilesHttpGetStoragesQueryPatched;
  label?: string;
  subtitle?: string;
  vertical?: boolean;
  isMulti?: boolean;
  canClear?: boolean;
  disabled?: boolean;
  /**
   * When true, groups are stored without itemTermNames so all items fall back
   * to the field's own term name. Use this for collection filters where all
   * storages share a single flat term (e.g. storage_id) rather than nested
   * per-purpose paths (files.storage_id, proxies.storage_id, etc.).
   */
  omitItemTermNames?: boolean;
  /**
   * When true, each group also carries additionalTermNames: ['storage_id'] so
   * the criteria builder ORs in a flat storage_id term alongside the nested
   * per-purpose terms. This ensures collections mapped to the selected storage
   * appear in search results alongside assets.
   */
  includeCollectionsTerm?: boolean;
  /**
   * Restricts which modifier options are shown in the modifier dropdown.
   * When undefined, all modifiers are available.
   */
  availableModifiers?: MultiSelectFilterModifier[];
};

export const MultiSelectFilterStorageField: FC<Props> = (props) => {
  const { loadOptions, resolveSelectedItems, itemLabels, enrichValue } =
    useStorageFilterOptions({
      params: props.params,
      includeCollectionsTerm: props.includeCollectionsTerm,
      omitItemTermNames: props.omitItemTermNames,
      value: props.value,
    });

  return (
    <MultiSelectFilterFieldWrapper
      name={props.name}
      value={props.value}
      onChange={props.onChange}
      filterValue={enrichValue}
      label={props.label}
      subtitle={props.subtitle}
      vertical={props.vertical}
      disabled={props.disabled}
      availableModifiers={props.availableModifiers}
      loadOptions={loadOptions}
      resolveSelectedItems={resolveSelectedItems}
      itemLabels={itemLabels}
    />
  );
};
